import { useEffect, useState } from 'react';
import CreatorCard from './CreatorCard';

type Creator = {
  id: string;
  full_name?: string | null;
  brand_name?: string | null;
  profile_photo_url?: string | null;
  country?: string | null;
  city?: string | null;
  primary_niche?: string | null;
  total_followers?: number | null;
  avg_engagement_rate?: number | null;
};

type CreatorsResponse = {
  success: boolean;
  creators: Creator[];
  countries: string[];
  total: number;
  hasMore?: boolean;
};

type Props = {
  countryFilter?: string | null;
  limit?: number;
  showFilters?: boolean;
  title?: string;
};

const skeletonRows = Array.from({ length: 6 }, (_, index) => index);

export default function CreatorsDataTable({ 
  countryFilter: initialCountry = null, 
  limit = 20,
  showFilters = true,
  title = 'Creators'
}: Props) {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string>(initialCountry || 'ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (countryFilter !== 'ALL') {
          params.set('country', countryFilter);
        }
        params.set('limit', String(limit));
        params.set('offset', String((currentPage - 1) * limit));
        
        console.log('🟢 [Frontend] Fetching creators:', `/api/creators?${params}`);
        
        const response = await fetch(`/api/creators?${params}`, {
          signal: controller.signal,
        });
        
        console.log('🟢 [Frontend] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔴 [Frontend] Request failed:', response.status, errorText);
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data: CreatorsResponse = await response.json();
        
        console.log('🟢 [Frontend] Received data:', {
          creatorsCount: data.creators?.length ?? 0,
          total: data.total,
          countriesCount: data.countries?.length ?? 0,
          success: data.success
        });
        
        setCreators(data.creators ?? []);
        setTotalItems(data.total ?? 0);
        if (data.countries?.length) {
          setCountries(data.countries);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('🔴 [Frontend] Failed to load creators:', err);
          setError('Unable to load creators. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [countryFilter, currentPage, limit]);

  const totalPages = Math.ceil(totalItems / limit);

  const handleFilterChange = (newCountry: string) => {
    setCountryFilter(newCountry);
    setCurrentPage(1);
  };

  const renderRows = () => {
    if (loading) {
      return skeletonRows.map((row) => (
        <tr key={`skeleton-${row}`} className="animate-pulse">
          <td className="px-4 py-3">
            <div className="h-12 w-12 rounded-full bg-gray-200" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-48 rounded-full bg-gray-200" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-20 rounded-full bg-gray-200" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-32 rounded-full bg-gray-200" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-24 rounded-full bg-gray-200" />
          </td>
          <td className="px-4 py-3">
            <div className="h-8 w-24 rounded-full bg-gray-200" />
          </td>
        </tr>
      ));
    }

    if (error) {
      return (
        <tr>
          <td colSpan={6} className="px-4 py-6 text-center text-sm text-red-600">
            {error}
          </td>
        </tr>
      );
    }

    if (!creators.length) {
      return (
        <tr>
          <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
            No creators found for this filter.
          </td>
        </tr>
      );
    }

    return creators.map((creator) => {
      const name = creator.full_name || creator.brand_name || 'Unknown creator';
      return (
        <tr
          key={creator.id}
          onClick={() => setSelectedCreatorId(creator.id)}
          className="group cursor-pointer border-b border-gray-100 last:border-none hover:bg-gray-50 transition-colors"
          title="Click to view details"
        >
          <td className="px-4 py-4">
            {creator.profile_photo_url ? (
              <img
                src={creator.profile_photo_url}
                alt={name}
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-lg font-bold text-gray-400">{name.charAt(0)}</span>
              </div>
            )}
          </td>
          <td className="px-4 py-4">
            <div className="flex flex-col">
              <span className="text-base font-semibold text-black">{name}</span>
              {creator.city && (
                <span className="text-sm text-gray-500">{creator.city}</span>
              )}
            </div>
          </td>
          <td className="px-4 py-4 text-sm font-medium text-black">
            {creator.country || '—'}
          </td>
          <td className="px-4 py-4 text-sm text-gray-600">
            {creator.primary_niche || 'Travel'}
          </td>
          <td className="px-4 py-4 text-sm text-gray-600">
            {creator.total_followers !== null && creator.total_followers !== undefined
              ? creator.total_followers.toLocaleString()
              : '—'}
          </td>
          <td className="px-4 py-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCreatorId(creator.id);
              }}
              className="bg-black text-waybook-green px-4 py-2 text-xs font-semibold transition-colors hover:bg-gray-900 rounded-md"
            >
              View Profile
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="space-y-4 border border-gray-200 bg-white p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium uppercase tracking-wider text-gray-500">
            {title} {totalItems > 0 && `(${totalItems})`}
          </p>
          {showFilters && (
            <div className="flex items-center gap-2">
              <label htmlFor="country-filter" className="text-sm font-medium text-black">
                Country
              </label>
              <select
                id="country-filter"
                value={countryFilter}
                onChange={(event) => handleFilterChange(event.target.value)}
                className="border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-black focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="ALL">All</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-3 font-medium">Photo</th>
                <th className="px-4 py-3 font-medium">Creator</th>
                <th className="px-4 py-3 font-medium">Country</th>
                <th className="px-4 py-3 font-medium">Niche</th>
                <th className="px-4 py-3 font-medium">Followers</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="bg-black text-waybook-green px-4 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
            >
              Previous
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="bg-black text-waybook-green px-4 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Creator Detail Modal */}
      {selectedCreatorId && (
        <CreatorCard
          creatorId={selectedCreatorId}
          onClose={() => setSelectedCreatorId(null)}
        />
      )}
    </>
  );
}
